import { useState } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@/components/ui/collapsible";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Link2, Plus, Trash2, ExternalLink, Pencil, ChevronDown, ChevronRight } from "lucide-react";
import { useClaimLinks } from "@/hooks/use-claim-links";
import type { ClaimLink } from "@/hooks/use-claim-links";
import { useToast } from "@/hooks/use-toast";

interface LinksModuleProps {
    claimId: number;
    isOpen: boolean;
    onToggle: () => void;
    canWrite: boolean;
}

function isValidUrl(str: string): boolean {
    try {
        const u = new URL(str);
        return u.protocol === "http:" || u.protocol === "https:";
    } catch {
        return false;
    }
}

export function LinksModule({ claimId, isOpen, onToggle, canWrite }: LinksModuleProps) {
    const { links, isLoading, createLink, deleteLink, updateLink } = useClaimLinks(claimId);
    const { toast } = useToast();
    const [showCreateModal, setShowCreateModal] = useState(false);
    const [label, setLabel] = useState("");
    const [url, setUrl] = useState("");
    const [editingLink, setEditingLink] = useState<ClaimLink | null>(null);
    const [editLabel, setEditLabel] = useState("");
    const [editUrl, setEditUrl] = useState("");

    const handleSubmit = async () => {
        if (!label.trim() || !url.trim()) return;
        if (!isValidUrl(url.trim())) {
            toast({ title: "Invalid URL", description: "Please enter a valid URL starting with http:// or https://", variant: "destructive" });
            return;
        }
        try {
            await createLink.mutateAsync({ label: label.trim(), url: url.trim() });
            toast({ title: "Link added", description: `"${label.trim()}" has been saved.` });
            setLabel("");
            setUrl("");
            setShowCreateModal(false);
        } catch {
            toast({ title: "Failed to add link", description: "Something went wrong. Please try again.", variant: "destructive" });
        }
    };

    const startEdit = (link: ClaimLink) => {
        setEditingLink(link);
        setEditLabel(link.label);
        setEditUrl(link.url);
    };

    const cancelEdit = () => {
        setEditingLink(null);
        setEditLabel("");
        setEditUrl("");
    };

    const handleUpdate = async () => {
        if (!editingLink || !editLabel.trim() || !editUrl.trim()) return;
        if (!isValidUrl(editUrl.trim())) {
            toast({ title: "Invalid URL", description: "Please enter a valid URL starting with http:// or https://", variant: "destructive" });
            return;
        }
        try {
            await updateLink.mutateAsync({ linkId: editingLink.id, data: { label: editLabel.trim(), url: editUrl.trim() } });
            toast({ title: "Link updated" });
            cancelEdit();
        } catch {
            toast({ title: "Failed to update link", description: "Something went wrong. Please try again.", variant: "destructive" });
        }
    };

    return (
        <>
            <Card className="shadow-xs w-full overflow-hidden">
                <Collapsible open={isOpen} onOpenChange={onToggle}>
                    <CollapsibleTrigger className="w-full">
                        <CardHeader className="py-3 px-4 hover:bg-muted/50 cursor-pointer transition-colors overflow-hidden">
                            <div className="flex items-center justify-between min-w-0 w-full">
                                <div className="flex items-center gap-2 min-w-0">
                                    <Link2 className="h-4 w-4 shrink-0" />
                                    <CardTitle className="text-sm font-medium truncate">Links</CardTitle>
                                    <Badge variant="secondary" className="text-xs h-5 px-1.5 min-w-[1.25rem] shrink-0 flex items-center justify-center">
                                        {links.length}
                                    </Badge>
                                </div>
                                {isOpen ? <ChevronDown className="h-4 w-4 shrink-0 text-muted-foreground" /> : <ChevronRight className="h-4 w-4 shrink-0 text-muted-foreground" />}
                            </div>
                        </CardHeader>
                    </CollapsibleTrigger>
                    <CollapsibleContent>
                        <CardContent className="px-4 pb-4 pt-0 w-full overflow-hidden">
                            <div className="pb-2 border-b mb-1"></div>

                            {isLoading ? (
                                <p className="text-xs text-muted-foreground py-2">Loading...</p>
                            ) : links.length === 0 ? (
                                <p className="text-xs text-muted-foreground py-2">No links yet</p>
                            ) : (
                                <div className="space-y-1.5 pt-1 max-h-[260px] overflow-y-auto pr-1 overflow-x-hidden scrollbar-thin scrollbar-thumb-muted-foreground/20 hover:scrollbar-thumb-muted-foreground/40 w-full max-w-full">
                                    {links.map((link) => (
                                        <div key={link.id} className="group flex items-center gap-2 py-0.5">
                                            <ExternalLink className="h-3.5 w-3.5 text-muted-foreground shrink-0" />
                                            <a
                                                href={link.url}
                                                target="_blank"
                                                rel="noopener noreferrer"
                                                className="text-xs text-primary hover:underline truncate flex-1"
                                                title={link.url}
                                            >
                                                {link.label}
                                            </a>
                                            <Button
                                                variant="ghost"
                                                size="sm"
                                                className="h-5 w-5 p-0 opacity-0 group-hover:opacity-100 group-focus-within:opacity-100 focus-visible:opacity-100 transition-opacity shrink-0"
                                                onClick={() => startEdit(link)}
                                                disabled={!canWrite}
                                                aria-label={`Edit link ${link.label}`}
                                            >
                                                <Pencil className="h-3 w-3 text-muted-foreground hover:text-primary" />
                                            </Button>
                                            <Button
                                                variant="ghost"
                                                size="sm"
                                                className="h-5 w-5 p-0 opacity-0 group-hover:opacity-100 group-focus-within:opacity-100 focus-visible:opacity-100 transition-opacity shrink-0"
                                                onClick={() => deleteLink.mutate(link.id)}
                                                disabled={!canWrite}
                                                aria-label={`Delete link ${link.label}`}
                                            >
                                                <Trash2 className="h-3 w-3 text-muted-foreground hover:text-destructive" />
                                            </Button>
                                        </div>
                                    ))}
                                </div>
                            )}

                            <div className="pt-3 border-t mt-3 w-full">
                                <Button
                                    size="sm"
                                    className="w-full h-8 text-xs"
                                    onClick={() => canWrite && setShowCreateModal(true)}
                                    disabled={!canWrite}
                                >
                                    <Plus className="h-3 w-3 mr-2" />
                                    Add Link
                                </Button>
                            </div>
                        </CardContent>
                    </CollapsibleContent>
                </Collapsible>
            </Card>

            <Dialog open={showCreateModal} onOpenChange={(open) => { if (!open || !canWrite) { setShowCreateModal(false); setLabel(""); setUrl(""); } }}>
                <DialogContent className="sm:max-w-md">
                    <DialogHeader>
                        <DialogTitle>Add Link</DialogTitle>
                    </DialogHeader>
                    <div className="space-y-3 py-2">
                        <Input
                            placeholder="Label"
                            aria-label="Link label"
                            value={label}
                            onChange={(e) => setLabel(e.target.value)}
                            disabled={!canWrite}
                        />
                        <Input
                            placeholder="https://..."
                            aria-label="Link URL"
                            value={url}
                            onChange={(e) => setUrl(e.target.value)}
                            disabled={!canWrite}
                        />
                    </div>
                    <DialogFooter>
                        <Button variant="ghost" onClick={() => { setShowCreateModal(false); setLabel(""); setUrl(""); }}>
                            Cancel
                        </Button>
                        <Button
                            onClick={handleSubmit}
                            disabled={!canWrite || createLink.isPending || !label.trim() || !url.trim()}
                        >
                            Save
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>

            <Dialog open={!!editingLink} onOpenChange={(open) => { if (!open) cancelEdit(); }}>
                <DialogContent className="sm:max-w-md">
                    <DialogHeader>
                        <DialogTitle>Edit Link</DialogTitle>
                    </DialogHeader>
                    <div className="space-y-3 py-2">
                        <Input
                            value={editLabel}
                            onChange={(e) => setEditLabel(e.target.value)}
                            aria-label="Edit link label"
                            placeholder="Label"
                            disabled={!canWrite}
                        />
                        <Input
                            value={editUrl}
                            onChange={(e) => setEditUrl(e.target.value)}
                            aria-label="Edit link URL"
                            placeholder="https://..."
                            disabled={!canWrite}
                        />
                    </div>
                    <DialogFooter>
                        <Button variant="ghost" onClick={cancelEdit}>
                            Cancel
                        </Button>
                        <Button
                            onClick={handleUpdate}
                            disabled={!canWrite || updateLink.isPending || !editLabel.trim() || !editUrl.trim()}
                        >
                            Save
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </>
    );
}